import { useState } from 'react';
import { Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { Permission, type PermissionKey } from '@/shared/constants/permissions';
import AdminNavDropdown from './AdminNavDropdown';

type NavEntry =
  | { type: 'link'; to: string; label: string; perm: PermissionKey }
  | { type: 'dropdown'; label: string; items: { to: string; label: string; perm: PermissionKey }[] };

const navEntries: NavEntry[] = [
  {
    type: 'dropdown',
    label: 'Blog',
    items: [
      { to: '/admin/posts', label: 'Posts', perm: Permission.POSTS_READ },
      { to: '/admin/comments', label: 'Comments', perm: Permission.COMMENTS_MODERATE },
      { to: '/admin/tags', label: 'Tags', perm: Permission.TAGS_MANAGE },
      { to: '/admin/categories', label: 'Categories', perm: Permission.CATEGORIES_MANAGE },
      { to: '/admin/series', label: 'Series', perm: Permission.TAGS_MANAGE },
    ],
  },
  {
    type: 'dropdown',
    label: 'Content',
    items: [
      { to: '/admin/cv', label: 'CV', perm: Permission.CV_MANAGE },
      { to: '/admin/news', label: 'News', perm: Permission.NEWS_MANAGE },
      { to: '/admin/media', label: 'Media', perm: Permission.UPLOADS_WRITE },
      { to: '/admin/files', label: 'Files', perm: Permission.UPLOADS_WRITE },
    ],
  },
  { type: 'link', to: '/admin/users', label: 'Users', perm: Permission.USERS_MANAGE },
  { type: 'link', to: '/admin/analytics', label: 'Analytics', perm: Permission.ANALYTICS_VIEW },
  {
    type: 'dropdown',
    label: 'Email',
    items: [
      { to: '/admin/email/compose', label: 'Compose', perm: Permission.EMAIL_SEND },
      { to: '/admin/email/templates', label: 'Templates', perm: Permission.EMAIL_TEMPLATES_WRITE },
      { to: '/admin/gmail', label: 'Gmail', perm: Permission.EMAIL_SEND },
    ],
  },
  { type: 'link', to: '/admin/ai', label: 'AI Content', perm: Permission.AI_CONTENT_VIEW },
  {
    type: 'dropdown',
    label: 'System',
    items: [
      { to: '/admin/system', label: 'System', perm: Permission.SYSTEM_VIEW },
      { to: '/admin/logs', label: 'Logs', perm: Permission.SYSTEM_VIEW },
      { to: '/admin/oci-repeater', label: 'OCI Repeater', perm: Permission.OCI_REPEATER_MANAGE },
      { to: '/admin/claudex', label: 'Claudex Shares', perm: Permission.CLAUDEX_SHARING },
    ],
  },
];

export default function AdminLayout() {
  const { isAuthenticated, isAdmin, username, logout, hasPermission } = useAuthStore();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [prevPathname, setPrevPathname] = useState<string | null>(null);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setMobileOpen(false);
    setOpenDropdown(null);
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  // Filter entries by permission
  const visibleEntries = navEntries
    .map((entry) => {
      if (entry.type === 'link') {
        return hasPermission(entry.perm) ? entry : null;
      }
      const visibleItems = entry.items.filter((item) => hasPermission(item.perm));
      return visibleItems.length > 0 ? { ...entry, items: visibleItems } : null;
    })
    .filter(Boolean) as NavEntry[];

  // Flat list for mobile menu
  const mobileGroups = visibleEntries.map((entry) => {
    if (entry.type === 'link') {
      return { label: null, items: [{ to: entry.to, label: entry.label }] };
    }
    return { label: entry.label, items: entry.items.map((i) => ({ to: i.to, label: i.label })) };
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="py-3 no-print" style={{ backgroundColor: 'rgba(245,245,245,0.05)', borderBottom: '1px solid rgba(245,245,245,0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="font-bold text-accent hover:underline no-underline">
              Admin Panel
            </Link>
            {/* Desktop nav */}
            <nav className="hidden md:flex gap-4 text-sm" role="navigation" aria-label="Admin navigation">
              {visibleEntries.map((entry) => {
                if (entry.type === 'link') {
                  const isActive = pathname === entry.to || pathname.startsWith(entry.to + '/');
                  return (
                    <Link
                      key={entry.to}
                      to={entry.to}
                      className={isActive ? 'text-accent font-bold' : 'text-muted hover:text-accent'}
                      {...(isActive ? { 'aria-current': 'page' as const } : {})}
                    >
                      {entry.label}
                    </Link>
                  );
                }
                return (
                  <AdminNavDropdown
                    key={entry.label}
                    label={entry.label}
                    items={entry.items}
                    isOpen={openDropdown === entry.label}
                    onToggle={() => setOpenDropdown(openDropdown === entry.label ? null : entry.label)}
                    pathname={pathname}
                  />
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted hidden sm:inline">{username}</span>
            <Link to="/" className="text-muted hover:text-accent hidden sm:inline">View Site</Link>
            <button
              onClick={logout}
              className="text-muted hover:text-accent bg-transparent border-none cursor-pointer font-mono hidden sm:inline"
            >
              Logout
            </button>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden bg-transparent cursor-pointer flex items-center justify-center"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{ color: 'var(--color-accent)', border: '1px solid var(--color-border)', width: '28px', height: '28px' }}
            >
              {mobileOpen ? <X size={14} /> : <Menu size={14} />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown */}
        <div
          className="md:hidden overflow-hidden transition-all duration-200"
          style={{ maxHeight: mobileOpen ? '500px' : '0px' }}
        >
          <div className="max-w-7xl mx-auto px-6 pt-3 pb-1 flex flex-col gap-1 text-sm" style={{ borderTop: '1px solid rgba(245,245,245,0.1)' }}>
            {mobileGroups.map((group, gi) => (
              <div key={gi}>
                {group.label && (
                  <span className="text-xs uppercase text-muted font-mono tracking-wider block mt-2 mb-1">
                    {group.label}
                  </span>
                )}
                {group.items.map((item) => {
                  const isActive = pathname === item.to || pathname.startsWith(item.to + '/');
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`block py-1 ${group.label ? 'pl-3' : ''} ${
                        isActive ? 'text-accent font-bold' : 'text-muted hover:text-accent'
                      }`}
                      {...(isActive ? { 'aria-current': 'page' as const } : {})}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            ))}
            <hr className="border-border my-1" />
            <span className="text-muted text-xs">{username}</span>
            <Link to="/" className="text-muted hover:text-accent py-1">View Site</Link>
            <button
              onClick={logout}
              className="text-muted hover:text-accent bg-transparent border-none cursor-pointer font-mono text-sm p-0 text-left py-1"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto px-6 w-full py-8">
        <Outlet />
      </main>
    </div>
  );
}
